import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import axiosRetry from 'axios-retry'
import { v4 as uuidv4 } from 'uuid'

import { setToast } from '@/entities/toast'
import { clearUserSession } from '@/entities/user/sessionSlice'
import { FASTMILE_API_HOST, FASTMILE_API_KEY } from '@/shared/config/consts'
import { tokenRefreshManager } from '@/shared/lib/tokenRefreshManager'

import { store } from '../../store'

export const FASTMILE_INSTANCE = axios.create({
  baseURL: FASTMILE_API_HOST,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Api-Key': FASTMILE_API_KEY,
  },
})

axiosRetry(FASTMILE_INSTANCE, {
  retries: 1,
  retryDelay: (retryCount) => {
    return retryCount * 200
  },
  retryCondition: (error) => {
    return error.response === undefined || [408, 429, 500, 502, 503, 504].includes(error.response?.status || 0)
  },
})

// Request interceptor with token refresh
FASTMILE_INSTANCE.interceptors.request.use(
  async (config) => {
    const state = store.getState()
    const { token, accessToken } = state.session
    const { activeGroupId } = state.userInterface

    if (activeGroupId) {
      config.headers.set('X-Group', activeGroupId.toString())
    }

    if (!config.headers['X-Request-ID']) {
      config.headers['X-Request-ID'] = uuidv4()
    }

    // Skip token refresh for auth endpoints
    if (config.url?.includes('/api/Auth/')) {
      // For auth endpoints, just add existing tokens
      if (accessToken) {
        config.headers.set('Authorization', `Bearer ${accessToken}`)
      }

      if (token) {
        config.headers.set('X-Auth-Token', token)
      }

      return config
    }

    try {
      await tokenRefreshManager.ensureValidToken()

      // Get fresh state after potential refresh
      const freshState = store.getState()
      const freshAccessToken = freshState.session.accessToken
      const freshToken = freshState.session.token

      if (freshAccessToken) {
        config.headers.set('Authorization', `Bearer ${freshAccessToken}`)
      }

      if (freshToken) {
        config.headers.set('X-Auth-Token', freshToken)
      }
    } catch (error) {
      // If token refresh fails, proceed with existing tokens (will likely fail with 401)
      if (accessToken) {
        config.headers.set('Authorization', `Bearer ${accessToken}`)
      }

      if (token) {
        config.headers.set('X-Auth-Token', token)
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Response interceptor with 401 handling
FASTMILE_INSTANCE.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Handle 401 Unauthorized responses
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      if (originalRequest.url?.includes('/api/Auth/')) {
        return Promise.reject(error)
      }

      try {
        await tokenRefreshManager.refreshToken()

        // Update the original request with new token
        const state = store.getState()
        const { accessToken, token } = state.session

        if (accessToken) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
        }

        if (token) {
          originalRequest.headers['X-Auth-Token'] = token
        }

        // Retry the original request
        return await FASTMILE_INSTANCE(originalRequest)
      } catch (refreshError) {
        // Refresh failed, user needs to login again
        store.dispatch(clearUserSession())

        return Promise.reject(error)
      }
    }

    // Handle other errors
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      const errorMessage = error.response?.data?.message
        ? `<strong>Message:</strong> ${error.response.data.message}`
        : `An error returned from the server: ${error.message}.`

      store.dispatch(setToast({
        open: true,
        severity: 'error',
        title: `Error ${error.code || error.response?.status || ''}`,
        content: `${errorMessage}<br />
          <strong>Method:</strong> ${error.config?.method?.toUpperCase()}<br />
          <strong>Endpoint:</strong> ${error.config?.url}
        `,
      }))
    }

    return Promise.reject(error)
  },
)

export const fastmileInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
  const source = axios.CancelToken.source()
  const promise = FASTMILE_INSTANCE({
    ...config,
    cancelToken: source.token,
  }).then((response: AxiosResponse<T>) => response.data)

  return promise
}